<template>
  <basic-popover :auto-hide="true" :placement="position" offset="35,15" popoverClass="basic-popover small">
    <job-apply-button-container :smallButton="smallButton"></job-apply-button-container>

    <template slot="popover">
      <div class="job-apply-form form-container">
        <form-fields
          ref="apply"
          :fields="fields"
          @on-change-field="handleChangeField"
          @on-submit="handleApply">
        </form-fields>
        <cv-file-upload
          :files="resumeState.file"
          :loading="resumeState.loading"
          :error="resumeState.error"
          @on-file-attach="handleUploadResume"
          @on-file-remove="handleRemoveResume">
        </cv-file-upload>
        <div>
          <button
            v-translate
            :disabled="disabled"
            :class="{'is-loading': createPreApplicationLoadingState}"
            class="button is-secondary is-rounded submit-application-button"
            @click="handleApply">
            Submit application
          </button>
        </div>
      </div>
      <div class="login-block paragraph-s paragraph-dark-gray">
        {{ labels.haveAccount }} <a class="primary-link" @click="goToLoginWithApply()">{{ labels.login }}</a>
      </div>
    </template>
  </basic-popover>
</template>

<script>

import {isEmpty} from 'lodash';
import BasicPopover from 'top20common/components/BasicPopover';
import FormFields from 'top20common/components/form/FormFields';
import redirects from 'top20common/distribution/services/redirects';
import storage from 'top20common/distribution/services/storage';
import {mapGetters} from 'vuex';
import CvFileUpload from 'top20common/components/file-upload/CvFileUpload';

import validation from '@/services/validation';
import analyticsHelper from '@/utils/analytics-helper';

import JobApplyButtonContainer from './JobApplyButtonContainer';

const APPLY_FIELD = 'apply-info';
const EMAIL = 'email';
const PHONE = 'phone';

export default {
  props: {
    position: String,
    smallButton: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      applyData: this.$route.query.email || '',
      initialApplyDataValue: this.$route.query.email || '',
    };
  },
  computed: {
    ...mapGetters(['createPreApplicationLoadingState', 'resumeState',]),
    labels() {
      return {
        haveAccount: this.$gettext('Already have an account?'),
        login: this.$gettext('Login')
      };
    },
    fields() {
      let label = this.$gettext('Enter your email or phone number');
      if (this.$route.query.utm_medium === 'email') {
        label = this.$gettext('Enter your email');
      }
      return [
        {
          label: label,
          value: APPLY_FIELD,
          type: 'text',
          initialValue: this.initialApplyDataValue
        }
      ];
    },
    rules() {
      let field = this.applyDataType;
      let validationRules = {
        [EMAIL]: [validation.email],
        [PHONE]: [validation.phone]
      };

      return validationRules[field];
    },
    errors() {
      if (this.applyData) {
        let error = validation.validateField(this.rules, this.applyData);
        return error ? {[APPLY_FIELD]: [error]} : {};
      }
    },
    applyDataType() {
      if (this.applyData) {
        return this.isEmail(this.applyData) ? EMAIL : PHONE;
      }

      return EMAIL;
    },
    disabled() {
      return !this.applyData || !isEmpty(this.errors);
    },
    jobApplyContactData() {
      return {
        'contact info': this.applyData,
        [this.applyDataType]: this.applyData,
        'job code': this.jobCode,
        source: analyticsHelper.getApplicationCreatedSource(this.$route.query)
      };
    },
    jobCode() {
      return this.$route.params.code;
    }
  },
  methods: {
    isEmail(value) {
      return !value.match(/^[\d-+\s()]+$/);
    },
    handleChangeField(e) {
      this.applyData = e.target.value;
    },
    handleApply() {
      if (!this.disabled) {
        let registerData = {[this.applyDataType]: this.applyData};
        if (this.resumeState.info) {
          const {email, phone, full_name, uid} = this.resumeState.info;
          registerData = {email, phone, full_name, resume: {uid}, ...registerData}
        }
        storage.setJsonItem('register-data', registerData);
        storage.setJsonItem('job-apply-data', this.jobApplyContactData);
        this.$emit('on-submit-apply-info');
      }
    },
    goToLoginWithApply() {
      redirects.saveTargetRouteBeforeLogin(`/my/dashboard?job_code=${this.jobCode}`);
      this.$store.commit('mutatePreviousAnchorState', this.$store.getters.jobsContainerIdState);
      redirects.goToCandidateLogin();
    },
    handleUploadResume(file) {
      if (this.resumeState.file !== file) {
        this.$store
          .dispatch('parseResumeAction', {file})
          .then(({email, phone, ...data}) => {
            this.applyData = email || phone;
            this.initialApplyDataValue = email || phone;
          })
          .catch(() => this.parseError());
      }
    },
    parseError() {
      this.$toast.open({
        message: this.$gettext("Our system couldn't read your CV file. Make sure you upload a text file."),
        type: 'is-danger'
      });
    },
    handleRemoveResume() {
      this.$store.commit('mutateResumeState', {file: null, info: null});
    },
  },
  components: {
    BasicPopover,
    JobApplyButtonContainer,
    FormFields,
    CvFileUpload
  }
};
</script>

<style lang="scss" scoped>
@import '~VariablesStyles';

.login-block {
  margin-top: 15px;
}

.job-apply-form .button.submit-application-button {
  width: 100%;
}

/deep/ .job-apply-form > div:last-child {
  text-align: right;
  margin-top: 20px;
}

@media (min-width: $tablet) {
  /deep/ .job-apply-form button {
    padding: 8px 15px;
  }
}
</style>
